import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CollapsiblePanel } from '@client/components/_ui/display/CollapsiblePanel';
import { User } from '@client/components/_ui/display/User';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { confirmDestructive } from '@client/components/_ui/feedback/modal';
import { AwaitingProductGate } from '@client/components/claims/product-binding/ClaimAwaitingProduct';
//import { InvoiceEdit } from './InvoiceEdit';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { EXPENSE_ITEM_STATUSES, type ExpenseItemStatus } from '@shared/constants';
import { isClaimAwaitingProduct } from '@shared/utils/business/claim-product-model';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import type { MenuProps } from 'antd';
import { Col, Dropdown, Row, Space } from 'antd';
import { MoreVertical } from 'lucide-react';
import { type Key, useState } from 'react';
import { claimFormStore } from '../store';
import { Invoices } from './Invoices';

export const Route = createFileRoute('/console/$orgId/claims/details/$id/expenses')({
    component: ExpensesRoute,
});

/** A claim awaiting its product has no cost codes yet, so its expenses wait for the product (CUR-2163). */
function ExpensesRoute() {
    const claim = claimFormStore.use.formData();
    if (!isClaimAwaitingProduct(claim)) return <Expenses />;
    return (
        <PageController route={Route}>
            <AwaitingProductGate
                claim={claim}
                title="Expenses and invoices need the product"
                description="Expenses and invoices are coded to the product's cost codes."
            >
                {null}
            </AwaitingProductGate>
        </PageController>
    );
}

type ExpenseItem = RouterOutputs['expenseItem']['listExpenseItems']['rows'][number];

type MenuItem = Required<MenuProps>['items'][number];

type QueryParams = RouterInputs['expenseItem']['listExpenseItems'];

const columnHelper = createColumnHelper<
    ExpenseItem,
    NonNullable<RouterInputs['expenseItem']['listExpenseItems']['orderBy']>
>();

function Expenses() {
    const claim = claimFormStore.use.formData();
    const orgId = useOrgId();
    const user = useUser();
    const isReadonly = claimFormStore.use.isReadonly();
    const { mutate: deleteExpenseItem, isPending: isPendingDeleteExpenseItem } =
        trpc.expenseItem.deleteExpenseItem.useMutation();
    const { mutate: updateExpenseItem, isPending: isPendingUpdateExpenseItem } =
        trpc.expenseItem.updateExpenseItem.useMutation();
    const { formatCurrency, formatDate } = useFormatter();
    const navigate = useNavigate();

    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const [, setSelectedRows] = useState<ExpenseItem[]>([]);
    const [, setOpenInvoiceDrawer] = useState(false);
    // const [invoiceIdToEdit, setInvoiceIdToEdit] = useState(-1);

    const [input, setInput] = useState<QueryParams>({
        where: {
            claimId: claim.id,
        },
    });

    const { data, isPending, error, refetch } = trpc.expenseItem.listExpenseItems.useQuery(input);

    const getActionItems = (record: ExpenseItem): MenuItem[] => {
        const items: MenuItem[] = [
            {
                key: 'edit',
                label: 'Edit',
                onClick: () => {
                    navigate({
                        to: '/console/$orgId/claims/details/$id/expenses/edit/$expenseItemId',
                        params: {
                            orgId,
                            id: claim.id,
                            expenseItemId: record.id,
                        },
                    });
                },
            },
            {
                key: 'delete',
                label: 'Delete',
                danger: true,
                onClick() {
                    void confirmDestructive({
                        title: 'Delete this expense item?',
                        content: 'The expense item and its amounts are removed from the claim.',
                        onOk: () => {
                            message.loading('Deleting...');

                            deleteExpenseItem(
                                { id: record.id },
                                {
                                    onSuccess() {
                                        message.success('Deleted.');
                                        void refetch();
                                    },
                                    onError(error) {
                                        errorMessage.show(error);
                                    },
                                },
                            );
                        },
                    });
                },
            },
        ];

        if (user.roles.includes('org_admin')) {
            items.splice(-1, 0, {
                key: 'update_status',
                label: 'Update Status',
                children: Object.keys(EXPENSE_ITEM_STATUSES).map(
                    (status): MenuItem => ({
                        key: status,
                        label: status,
                        onClick() {
                            message.loading('Saving...');

                            updateExpenseItem(
                                {
                                    id: record.id,
                                    set: { status: status as ExpenseItemStatus },
                                },
                                {
                                    onSuccess() {
                                        message.success('Saved.');
                                        void refetch();
                                    },
                                    onError(error) {
                                        errorMessage.show(error);
                                    },
                                },
                            );
                        },
                    }),
                ),
            });
        }

        if (record.status === 'approved' && user.roles.includes('org_admin')) {
            items.splice(-1, 0, {
                key: 'invoice',
                label: 'Invoice',
                onClick: () => {
                    setSelectedRows([record]);
                    setOpenInvoiceDrawer(true);
                },
            });
        }

        return items;
    };

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending || isPendingUpdateExpenseItem || isPendingDeleteExpenseItem,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: (params) => {
            setInput({
                ...params,
                where: {
                    claimId: claim.id,
                },
            });
        },
        // The page shows expenses and invoices, so each table has its own key (CUR-1341).
        urlKey: 'expenses',
        queryError: error,
        pagination: {
            position: 'bottom',
        },
        // `claimId` is the fixed scope, not a user filter, so an empty list means no expenses yet.
        emptyState: { hasActiveFilters: false, noRecordsTitle: 'No expenses yet' },
        rowSelection:
            !isReadonly && user.roles.includes('org_admin')
                ? {
                      enabled: true,
                      columnTitle: 'Create Invoice',
                      onChange: (selectedRowKeys, selectedRows) => {
                          setSelectedRowKeys(selectedRowKeys);
                          setSelectedRows(selectedRows);
                      },
                      selectedRowKeys,
                      getCheckboxProps: (record) => {
                          return record.status !== 'approved' ? { disabled: true } : {};
                      },
                      columnWidth: 100,
                  }
                : undefined,
        columns: [
            columnHelper.accessor((row) => row.ExpenseType?.name, {
                id: 'expenseType',
                header: 'Expense item',
                sortable: 'ExpenseType.name',
                cell: ({ row }) => row.ExpenseType?.name,
            }),
            columnHelper.accessor('date', {
                id: 'date',
                header: 'Date',
                sortable: 'date',
                cell: ({ row }) => formatDate(row.date),
            }),
            columnHelper.accessor('userId', {
                id: 'userId',
                header: 'User',
                sortable: 'userId',
                cell: ({ row }) => <User userId={row.userId} />,
            }),
            columnHelper.accessor('rateCode', {
                id: 'rateCode',
                header: 'Rate code',
                sortable: 'rateCode',
                cell: ({ row }) => row.rateCode,
            }),
            columnHelper.accessor('note', {
                id: 'note',
                header: 'Notes',
                sortable: 'note',
                cell: ({ row }) => row.note,
            }),
            columnHelper.accessor('qty', {
                id: 'qty',
                header: 'Quantity',
                sortable: 'qty',
                cell: ({ row }) => String(row.qty),
            }),
            columnHelper.accessor('rate', {
                id: 'rate',
                header: 'Rate',
                sortable: 'rate',
                cell: ({ row }) => formatCurrency(row.rate),
            }),
            columnHelper.display({
                id: 'total',
                header: 'Total',
                cell: ({ row }) => formatCurrency(row.rate.mul(row.qty)),
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'Status',
                sortable: 'status',
                cell: ({ row }) => row.status,
            }),
            columnHelper.actions({
                cell: ({ row }) =>
                    !isReadonly && (
                        <Dropdown
                            trigger={['click']}
                            onOpenChange={(visible) => {
                                if (visible) {
                                    setSelectedRowKeys((prev) => prev.filter((id) => id !== row.id));
                                    setSelectedRows((prev) => prev.filter((item) => item.id !== row.id));
                                }
                            }}
                            disabled={row.status === 'invoiced'}
                            menu={{ items: getActionItems(row) }}
                            placement="bottomRight"
                            arrow
                        >
                            <Button.Icon
                                aria-label="Expense actions"
                                icon={<MoreVertical />}
                                color="gray"
                                size="small"
                            />
                        </Dropdown>
                    ),
            }),
        ],
    });
    return (
        <PageController route={Route}>
            <Outlet />

            {/*
            TODO: Fix this
            {!disabled && openInvoiceDrawer && (
                <InvoiceEdit
                    open={openInvoiceDrawer}
                    onClose={() => {
                        refetch(variables);
                        setOpenInvoiceDrawer(false);
                        setSelectedRowKeys([]);
                        setSelectedRowKeys([]);
                        setInvoiceIdToEdit(-1);
                    }}
                    idToEdit={invoiceIdToEdit}
                    claim={claim}
                    setIdToEdit={(id) => {
                        setInvoiceIdToEdit(id);
                        setSelectedRowKeys([]);
                        setSelectedRows([]);
                    }}
                    expenses={invoiceIdToEdit === -1 ? selectedRows : undefined}
                />
            )} */}

            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <PrismaTable
                        {...tableProps}
                        onRow={(record) => ({
                            className: record.status === 'invoiced' ? 'text-subtle-foreground' : '',
                        })}
                    >
                        <PrismaTable.Header
                            title={<div className="text-lg">Expenses</div>}
                            actions={
                                <Space className="flex">
                                    <CollapsiblePanel open={!!selectedRowKeys.length}>
                                        <Button
                                            type="primary"
                                            onClick={() => {
                                                setOpenInvoiceDrawer(true);
                                            }}
                                        >
                                            Invoice selected
                                        </Button>
                                    </CollapsiblePanel>
                                    <Button.Add.Link
                                        disabled={isReadonly}
                                        variant="tertiary"
                                        to="/console/$orgId/claims/details/$id/expenses/add"
                                        params={{
                                            orgId,
                                            id: claim.id,
                                        }}
                                        label="Add new expense"
                                    />
                                </Space>
                            }
                        />
                    </PrismaTable>
                </Col>
                {user.roles.includes('org_admin') && (
                    <Col span={24}>
                        <Invoices disabled={isReadonly} />
                    </Col>
                )}
            </Row>
        </PageController>
    );
}
